import React from 'react';

export const Pricing = () => {
  const plans = [
    {
      name: 'Pro',
      price: '$299',
      period: '/ month',
      description: 'For shipping teams',
      features: [
        'Semantic journeys',
        'Automatic topic clustering',
        '2 projects',
        '1M events / month',
        '30-day retention',
        'Email support',
      ],
      cta: 'Start free trial',
      highlighted: false,
    },
    {
      name: 'Business',
      price: '$799',
      period: '/ month',
      description: 'For scale & reporting',
      features: [
        'Everything in Pro, plus:',
        '10 projects',
        '10M events / month',
        '90-day retention',
        'Export APIs',
      ],
      cta: 'Start free trial',
      highlighted: true,
    },
  ];

  return (
    <section className="py-32 md:py-40 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-turret-purple/5 rounded-full blur-[120px]" />
      </div>

      <div className="section-container relative z-10">
        <div className="text-center mb-20">
          <p className="text-turret-purple font-semibold tracking-wide uppercase text-sm mb-4">
            Pricing
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 tracking-tight mb-6">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-neutral-500 max-w-xl mx-auto">
            Start with a 14-day free trial. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative group ${plan.highlighted ? 'md:-mt-4 md:mb-4' : ''}`}
            >
              {/* Glow effect for highlighted plan */}
              {plan.highlighted && (
                <div className="absolute -inset-px bg-gradient-to-b from-turret-purple to-turret-purple-light rounded-[32px] blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
              )}

              <div className={`relative h-full p-8 lg:p-10 rounded-[28px] border transition-all duration-500 flex flex-col ${
                plan.highlighted
                  ? 'bg-gradient-to-b from-neutral-900 to-neutral-950 border-turret-purple/30 shadow-2xl'
                  : 'bg-white border-slate-200/80 shadow-sm hover:shadow-xl hover:border-slate-300'
              }`}>
                {/* Popular badge */}
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-turret-purple to-turret-purple-light rounded-full shadow-lg shadow-turret-purple/25">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-8">
                  <h3 className={`text-2xl font-semibold mb-2 tracking-tight ${
                    plan.highlighted ? 'text-white' : 'text-neutral-900'
                  }`}>
                    {plan.name}
                  </h3>
                  <p className={plan.highlighted ? 'text-neutral-400' : 'text-neutral-500'}>
                    {plan.description}
                  </p>
                </div>

                <div className="mb-8">
                  <span className={`text-5xl lg:text-6xl font-bold tracking-tight ${
                    plan.highlighted ? 'text-white' : 'text-neutral-900'
                  }`}>
                    {plan.price}
                  </span>
                  <span className={`ml-2 ${plan.highlighted ? 'text-neutral-400' : 'text-neutral-500'}`}>
                    {plan.period}
                  </span>
                </div>

                <ul className="space-y-4 mb-10 flex-grow">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-4">
                      {feature.includes('Everything in') ? (
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                          plan.highlighted ? 'bg-turret-purple/20' : 'bg-turret-purple/10'
                        }`}>
                          <svg
                            className="w-3.5 h-3.5 text-turret-purple"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                      ) : (
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                          plan.highlighted ? 'bg-white/10' : 'bg-slate-100'
                        }`}>
                          <svg
                            className={`w-3.5 h-3.5 ${plan.highlighted ? 'text-turret-purple' : 'text-turret-purple'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <span className={`leading-relaxed ${
                        feature.includes('Everything in')
                          ? plan.highlighted ? 'text-white font-medium' : 'text-neutral-900 font-medium'
                          : plan.highlighted ? 'text-neutral-300' : 'text-neutral-600'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://dashboard.useturret.com"
                  className={`block w-full py-4 px-6 text-center font-semibold rounded-xl transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-turret-purple to-turret-purple-light text-white shadow-lg shadow-turret-purple/25 hover:shadow-xl hover:shadow-turret-purple/40 hover:scale-[1.02]'
                      : 'bg-neutral-900 text-white hover:bg-neutral-800 hover:scale-[1.02]'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-neutral-500 mt-12 text-lg">
          2 months free with annual plans
        </p>
      </div>
    </section>
  );
};
